import React, { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Leaf, Droplets, TreeDeciduous, Sun,
  BookOpen, Package, ArrowRight, ArrowDown,
  CheckCircle, Heart, Sparkles, Star,
  Gift, Music, Video, Users,
  ShoppingBag, Crown, Flame, Eye
} from "lucide-react";
import { Button } from "./ui/button";

/* ───────────────────────────────────────────────────────
   THE BOOK OF COLOX — Shopify Storefront Prototype
   "Divine Dark Feminine, Led by the Light"
   ─────────────────────────────────────────────────────── */

// === ASSET URLS ===
const ASSETS = {
  heroCollage: "https://customer-assets.emergentagent.com/job_81d71cca-4b17-45d1-850c-6c69dcd8d556/artifacts/dtvictv5_Untitled%20design%20seedz.png",
  seedWaterGrow: "https://customer-assets.emergentagent.com/job_portfolio-cms-18/artifacts/9bnb06ww_seed%20water%20grow.png",
  siren: "https://customer-assets.emergentagent.com/job_portfolio-cms-18/artifacts/rc8podi8_Siren%20Of%20Yahs%20Universe.png",
  yhwhIcon: "https://customer-assets.emergentagent.com/job_aligned-kotf/artifacts/o2eovbzb_YHWH%20Icon%202.2.png",
  yahffirmations: "https://customer-assets.emergentagent.com/job_aligned-kotf/artifacts/dnlg4hor_Yahffirmations%20-%20ART%20ENERGI.png",
  cathedral: "https://images.unsplash.com/photo-1637572177881-8650be30a539?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxjYXRoZWRyYWwlMjBsaWdodCUyMHJheXMlMjBzdGFpbmVkJTIwZ2xhc3MlMjBkYXJrfGVufDB8fHx8MTc3MjAzNTIxM3ww&ixlib=rb-4.1.0&q=85",
  seedPdf: "/downloads/seedz-shy-yahffirmations.pdf",
};

// === ANIMATIONS ===
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};
const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6 } },
};

// === HELPER: Animated Section ===
function AnimSection({ children, className = "", variants = fadeUp }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      variants={variants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// === PRODUCT TIERS DATA ===
const TIERS = [
  {
    id: "plant",
    name: "PLANT",
    subtitle: "The Seed of Light",
    price: "$22.22",
    meaning: "New beginnings & balance",
    icon: Leaf,
    color: "#d4a254",
    items: [
      "Full Digital Coloring Book (PDF)",
      "30+ Sacred Designs",
      "Scripture Reflections",
      "Instant Download",
      "Print Unlimited Copies",
    ],
    cta: "Plant Your Seed",
    badge: null,
  },
  {
    id: "water",
    name: "WATER",
    subtitle: "Nourished by Spirit",
    price: "$33.33",
    meaning: "Creative expression",
    icon: Droplets,
    color: "#7eb8c9",
    items: [
      "Everything in PLANT, plus:",
      "Guided Meditation Audio (10 min)",
      "12 Printable Affirmation Cards",
      "Coloring Ritual Guide",
      "Deep Scripture References",
    ],
    cta: "Nourish Your Spirit",
    badge: "MOST LOVED",
  },
  {
    id: "grow",
    name: "GROW",
    subtitle: "Rooted in Community",
    price: "$44.44",
    meaning: "Foundation & growth",
    icon: TreeDeciduous,
    color: "#a3c97e",
    items: [
      "Everything in WATER, plus:",
      "Personal Welcome Video",
      "Creator Walkthrough Video",
      "Private Community Access",
      "Monthly Live Coloring Session",
    ],
    cta: "Join the Garden",
    badge: null,
  },
  {
    id: "harvest",
    name: "HARVEST",
    subtitle: "Sacred Keepsake",
    price: "$77.77",
    meaning: "Divine wisdom",
    icon: Crown,
    color: "#e8c87a",
    items: [
      "Everything in GROW, plus:",
      "Premium Physical Printed Book",
      "Certificate of Authenticity",
      "Handcrafted Bookmark Set",
      "Personalized Handwritten Note",
      "Exclusive Frameable Art Print",
    ],
    cta: "Receive the Harvest",
    badge: "SACRED EDITION",
  },
];

// === KEEPSAKE PRODUCTS ===
const KEEPSAKES = [
  {
    name: "Seed of Wisdom Journal",
    desc: "Guided prayer & reflection companion. 144 pages of sacred prompts aligned with each coloring page.",
    icon: BookOpen,
    price: "$33.33",
  },
  {
    name: "Yahffirmation Card Deck",
    desc: "52 hand-illustrated affirmation cards. One for each week of your divine year.",
    icon: Sparkles,
    price: "$22.22",
  },
  {
    name: "Anointing Oil & Incense Set",
    desc: "Frankincense, myrrh & cedarwood. Sacred scents to anoint your coloring ritual.",
    icon: Flame,
    price: "$28.88",
  },
  {
    name: "Divine Connection Bracelet",
    desc: "Handcrafted seed bead bracelet with sacred symbols. Each bead a prayer.",
    icon: Heart,
    price: "$18.88",
  },
  {
    name: "Meditation Candle Trio",
    desc: "Soy wax candles in Plant, Water & Grow scents. Set the atmosphere for sacred coloring.",
    icon: Sun,
    price: "$24.44",
  },
  {
    name: "Curated Color Pencil Set",
    desc: "24 artist-grade pencils. Colors chosen to match every design in the Book of Colox.",
    icon: Star,
    price: "$19.99",
  },
  {
    name: "Sacred Art Print Collection",
    desc: "5 frameable prints from the book. Museum-quality paper, ready to grace your walls.",
    icon: Eye,
    price: "$44.44",
  },
  {
    name: "Gift of Light Bundle",
    desc: "Coloring book + journal + candle + card deck. The perfect gift for a soul you love.",
    icon: Gift,
    price: "$88.88",
  },
];

// ═══════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════
const BookOfColox = () => {
  const [cartCount, setCartCount] = useState(0);
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  // Parallax effect for hero
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const addToCart = () => {
    setCartCount((c) => c + 1);
  };

  const handleCheckout = async (tierId) => {
    // Map tier IDs to package IDs
    const packageMap = { plant: "plant", water: "water", grow: "grow" };
    const packageId = packageMap[tierId];
    
    if (!packageId) {
      // Harvest tier — future
      alert("The Harvest tier is coming soon. Stay tuned!");
      return;
    }

    setCheckoutLoading(tierId);
    try {
      const originUrl = window.location.origin;
      const response = await fetch('/api/payments/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: packageId, origin_url: originUrl })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Checkout failed');
      }

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      alert('Unable to start checkout. Please try again.');
      console.error('Checkout error:', error);
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div
      data-testid="book-of-colox-page"
      className="min-h-screen"
      style={{
        background: "#0a0a0f",
        fontFamily: "'Cormorant Garamond', Georgia, serif",
      }}
    >
      {/* ── Google Fonts Inject ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Raleway:wght@300;400;500;600&display=swap');
        
        .colox-serif { font-family: 'Cormorant Garamond', Georgia, serif; }
        .colox-sans { font-family: 'Raleway', 'Segoe UI', sans-serif; }
        
        .colox-glow {
          box-shadow: 0 0 60px rgba(212, 162, 84, 0.15), 
                      0 0 120px rgba(212, 162, 84, 0.05);
        }
        .colox-text-glow {
          text-shadow: 0 0 40px rgba(212, 162, 84, 0.3);
        }
        .colox-divider {
          background: linear-gradient(90deg, transparent, rgba(212,162,84,0.4), transparent);
          height: 1px;
        }
        .colox-card {
          background: linear-gradient(160deg, rgba(26,21,37,0.9), rgba(15,12,20,0.95));
          border: 1px solid rgba(212,162,84,0.12);
          transition: all 0.4s ease;
        }
        .colox-card:hover {
          border-color: rgba(212,162,84,0.3);
          box-shadow: 0 8px 40px rgba(212,162,84,0.08);
          transform: translateY(-4px);
        }
        .colox-btn-gold {
          background: linear-gradient(135deg, #d4a254, #e8c87a);
          color: #0a0a0f;
          font-family: 'Raleway', sans-serif;
          font-weight: 600;
          letter-spacing: 0.05em;
          transition: all 0.3s ease;
        }
        .colox-btn-gold:hover {
          background: linear-gradient(135deg, #e8c87a, #f5d78e);
          box-shadow: 0 4px 24px rgba(212,162,84,0.3);
          transform: translateY(-1px);
        }
        .colox-btn-outline {
          border: 1px solid rgba(212,162,84,0.3);
          color: #d4a254;
          font-family: 'Raleway', sans-serif;
          font-weight: 500;
          letter-spacing: 0.05em;
          transition: all 0.3s ease;
          background: transparent;
        }
        .colox-btn-outline:hover {
          border-color: rgba(212,162,84,0.6);
          background: rgba(212,162,84,0.08);
        }
        
        @keyframes gentleFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes softPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
        .animate-float { animation: gentleFloat 6s ease-in-out infinite; }
        .animate-soft-pulse { animation: softPulse 4s ease-in-out infinite; }
      `}</style>

      {/* ══════════════════════════════════════
           ANNOUNCEMENT BAR
         ══════════════════════════════════════ */}
      {showAnnouncement && (
        <div
          data-testid="announcement-bar"
          className="relative colox-sans text-center py-2.5 px-4"
          style={{
            background: "linear-gradient(90deg, #1a1525, #1e1830, #1a1525)",
            borderBottom: "1px solid rgba(212,162,84,0.15)",
          }}
        >
          <p className="text-xs tracking-widest" style={{ color: "#d4a254" }}>
            SACRED LAUNCH &mdash; FREE SEED PAGES WITH EVERY ORDER
          </p>
          <button
            onClick={() => setShowAnnouncement(false)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-xs opacity-40 hover:opacity-80"
            style={{ color: "#d4a254" }}
          >
            &times;
          </button>
        </div>
      )}

      {/* ══════════════════════════════════════
           NAVIGATION
         ══════════════════════════════════════ */}
      <nav
        data-testid="colox-nav"
        className="sticky top-0 z-50 backdrop-blur-md"
        style={{
          background: "rgba(10,10,15,0.88)",
          borderBottom: "1px solid rgba(212,162,84,0.08)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Flame className="w-5 h-5" style={{ color: "#d4a254" }} />
            <span
              className="colox-serif text-lg font-semibold tracking-wider"
              style={{ color: "#e8c87a" }}
            >
              THE BOOK OF COLOX
            </span>
          </div>

          <div className="hidden md:flex items-center gap-8 colox-sans text-xs tracking-widest">
            {["THE BOOK", "TIERS", "KEEPSAKES", "OUR STORY"].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase().replace(/\s/g, "-")}`}
                className="hover:opacity-100 transition-opacity"
                style={{ color: "rgba(212,162,84,0.6)" }}
              >
                {item}
              </a>
            ))}
          </div>

          <button
            data-testid="cart-button"
            className="relative colox-btn-outline px-4 py-2 rounded-full text-xs"
          >
            <ShoppingBag className="w-4 h-4 inline mr-2" />
            BAG
            {cartCount > 0 && (
              <span
                className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ background: "#d4a254", color: "#0a0a0f" }}
              >
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </nav>

      {/* ══════════════════════════════════════
           HERO SECTION
         ══════════════════════════════════════ */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden"
        style={{ minHeight: "100vh" }}
      >
        {/* Background collage with parallax */}
        <div
          className="absolute inset-0"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        >
          <img
            src={ASSETS.heroCollage}
            alt="The Book of Colox divine art"
            className="w-full h-full object-cover"
            style={{ opacity: 0.25, filter: "saturate(0.6) brightness(0.5)" }}
          />
        </div>

        {/* Gradient overlays for depth */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(10,10,15,0.3) 0%, rgba(10,10,15,0.6) 40%, rgba(10,10,15,0.95) 85%, #0a0a0f 100%)",
          }}
        />
        {/* Golden light rays from top center */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% -20%, rgba(212,162,84,0.1) 0%, transparent 60%)",
          }}
        />

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 flex flex-col items-center justify-center" style={{ minHeight: "100vh" }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="text-center"
          >
            {/* Eyebrow */}
            <p
              className="colox-sans text-xs tracking-[0.35em] mb-6"
              style={{ color: "rgba(212,162,84,0.6)" }}
            >
              BY KEEPER OF THE FIYAH
            </p>

            {/* Main Title */}
            <h1
              className="colox-serif text-5xl sm:text-6xl lg:text-8xl font-light leading-tight mb-4 colox-text-glow"
              style={{ color: "#f5efe6" }}
            >
              The Book of{" "}
              <span
                className="font-semibold italic"
                style={{ color: "#d4a254" }}
              >
                Colox
              </span>
            </h1>

            {/* Divider */}
            <div className="colox-divider w-48 mx-auto my-6" />

            {/* Subtitle */}
            <p
              className="colox-serif text-xl sm:text-2xl italic font-light mb-3"
              style={{ color: "#c9b896" }}
            >
              "Divine dark feminine, led by the light"
            </p>

            <p
              className="colox-sans text-sm max-w-xl mx-auto mb-10 leading-relaxed"
              style={{ color: "rgba(201,184,150,0.6)" }}
            >
              A sacred coloring experience for the woman who walks in faith and
              dares to discover new inspiration. Each page is a prayer. Each
              color, a revelation.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="#tiers">
                <button
                  data-testid="hero-shop-btn"
                  className="colox-btn-gold px-8 py-3 rounded-full text-sm"
                >
                  ENTER THE GARDEN
                </button>
              </a>
              <a href="#the-book">
                <button className="colox-btn-outline px-8 py-3 rounded-full text-sm">
                  DISCOVER THE JOURNEY
                </button>
              </a>
            </div>

            {/* Scroll indicator */}
            <motion.div
              className="mt-16"
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 2.5 }}
            >
              <ArrowDown
                className="w-5 h-5 mx-auto"
                style={{ color: "rgba(212,162,84,0.3)" }}
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════
           SCRIPTURE INTERLUDE
         ══════════════════════════════════════ */}
      <section className="py-20 px-6" style={{ background: "#0a0a0f" }}>
        <AnimSection className="max-w-3xl mx-auto text-center">
          <Flame
            className="w-6 h-6 mx-auto mb-6 animate-soft-pulse"
            style={{ color: "#d4a254" }}
          />
          <blockquote
            className="colox-serif text-2xl sm:text-3xl italic font-light leading-relaxed mb-4"
            style={{ color: "#c9b896" }}
          >
            "Place me as a seal upon your heart... for love is as strong as
            death, its flames are a blazing fire, the flame of YAH."
          </blockquote>
          <p
            className="colox-sans text-xs tracking-widest"
            style={{ color: "rgba(212,162,84,0.5)" }}
          >
            SONG OF SOLOMON 8:6
          </p>
        </AnimSection>
      </section>

      {/* ══════════════════════════════════════
           ABOUT THE BOOK
         ══════════════════════════════════════ */}
      <section
        id="the-book"
        className="py-24 px-6 relative"
        style={{
          background:
            "linear-gradient(to bottom, #0a0a0f, #0f0c14, #0a0a0f)",
        }}
      >
        {/* Soft radial glow */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 30% 50%, rgba(212,162,84,0.04) 0%, transparent 50%)",
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto">
          <AnimSection className="text-center mb-16">
            <p
              className="colox-sans text-xs tracking-[0.3em] mb-4"
              style={{ color: "rgba(212,162,84,0.5)" }}
            >
              WHAT AWAITS WITHIN
            </p>
            <h2
              className="colox-serif text-4xl sm:text-5xl font-light"
              style={{ color: "#f5efe6" }}
            >
              More Than a Coloring Book
            </h2>
          </AnimSection>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left - Image */}
            <AnimSection>
              <div className="relative">
                <div className="colox-glow rounded-2xl overflow-hidden">
                  <img
                    src={ASSETS.siren}
                    alt="Siren of Yah's Universe"
                    className="w-full rounded-2xl"
                    style={{
                      filter: "brightness(0.85) saturate(0.9)",
                      maxHeight: "500px",
                      objectFit: "cover",
                    }}
                  />
                </div>
                {/* Floating accent */}
                <div
                  className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full animate-float"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(212,162,84,0.15), transparent 70%)",
                  }}
                />
              </div>
            </AnimSection>

            {/* Right - Text */}
            <AnimSection>
              <div className="space-y-6">
                <p
                  className="colox-serif text-xl italic"
                  style={{ color: "#d4a254" }}
                >
                  "She is Her, She is You, Executing as the BUTTERFLY"
                </p>
                <p
                  className="colox-sans text-sm leading-relaxed"
                  style={{ color: "rgba(201,184,150,0.7)" }}
                >
                  The Book of Colox is not simply pages to fill with color. It
                  is a sacred invitation &mdash; for the woman who has walked
                  faithfully and now feels a stirring for something deeper. Each
                  illustration is a doorway. Each affirmation, a whisper from the
                  divine.
                </p>
                <p
                  className="colox-sans text-sm leading-relaxed"
                  style={{ color: "rgba(201,184,150,0.7)" }}
                >
                  Rooted in scripture, inspired by the divine dark feminine
                  energy that has always existed within creation, this book
                  invites you to color your way into a new conversation with the
                  Almighty. You don't have to leave your faith. You simply open a
                  new room within it.
                </p>

                {/* Feature list */}
                <div className="space-y-3 pt-4">
                  {[
                    { icon: BookOpen, text: "30+ hand-curated sacred designs" },
                    { icon: Heart, text: "YAHFFIRMATIONS on every page" },
                    { icon: Music, text: "Scripture reflections & meditations" },
                    { icon: Sparkles, text: "A spiritual practice, not just art" },
                  ].map((f, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3"
                    >
                      <f.icon
                        className="w-4 h-4 flex-shrink-0"
                        style={{ color: "#d4a254" }}
                      />
                      <span
                        className="colox-sans text-sm"
                        style={{ color: "#c9b896" }}
                      >
                        {f.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimSection>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
           7 SACRED SECTIONS
         ══════════════════════════════════════ */}
      <section className="py-20 px-6" style={{ background: "#0a0a0f" }}>
        <AnimSection className="max-w-4xl mx-auto">
          <p
            className="colox-sans text-xs tracking-[0.3em] text-center mb-10"
            style={{ color: "rgba(212,162,84,0.5)" }}
          >
            7 SACRED SECTIONS INSIDE
          </p>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {[
              { title: "SHY, YOU", verse: "Isaiah 43:10" },
              { title: "FLAME", verse: "Song of Solomon 8:6" },
              { title: "REMEMBER THE SHY", verse: "2 Kings 20:3" },
              { title: "EYE TOUCH LOVE", verse: "1 John 4:8" },
              { title: "TRIBE TO INSPIYAH", verse: "Revelation 5:5" },
              { title: "YAHAWAH", verse: "Isaiah 43:11" },
              { title: "YHWH יהוה IS THE WAY", verse: "Isaiah 38:16" },
              { title: "BONUS", verse: "Dedication" },
            ].map((s, i) => (
              <motion.div
                key={i}
                variants={scaleIn}
                className="colox-card rounded-xl p-4 text-center"
              >
                <p
                  className="colox-serif text-sm font-semibold"
                  style={{ color: "#e8c87a" }}
                >
                  {s.title}
                </p>
                <p
                  className="colox-sans text-xs mt-1"
                  style={{ color: "rgba(212,162,84,0.4)" }}
                >
                  {s.verse}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </AnimSection>
      </section>

      {/* ══════════════════════════════════════
           PRODUCT TIERS
         ══════════════════════════════════════ */}
      <section
        id="tiers"
        className="py-24 px-6 relative"
        style={{
          background:
            "linear-gradient(to bottom, #0a0a0f, #100d18 50%, #0a0a0f)",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 30%, rgba(212,162,84,0.06) 0%, transparent 50%)",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto">
          <AnimSection className="text-center mb-16">
            <p
              className="colox-sans text-xs tracking-[0.3em] mb-4"
              style={{ color: "rgba(212,162,84,0.5)" }}
            >
              YOUR DIVINE CONNECTION JOURNEY
            </p>
            <h2
              className="colox-serif text-4xl sm:text-5xl font-light mb-4"
              style={{ color: "#f5efe6" }}
            >
              Choose Your Path
            </h2>
            <p
              className="colox-sans text-sm max-w-lg mx-auto"
              style={{ color: "rgba(201,184,150,0.5)" }}
            >
              Every soul's journey is unique. Whether you seek simplicity or a
              sacred keepsake experience, there is a tier designed for you.
            </p>
          </AnimSection>

          {/* Tier Cards */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {TIERS.map((tier) => (
              <motion.div
                key={tier.id}
                variants={fadeUp}
                data-testid={`tier-card-${tier.id}`}
                className="colox-card rounded-2xl p-6 flex flex-col relative"
              >
                {tier.badge && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 colox-sans text-xs tracking-widest px-4 py-1 rounded-full"
                    style={{
                      background:
                        "linear-gradient(135deg, #d4a254, #e8c87a)",
                      color: "#0a0a0f",
                      fontWeight: 600,
                    }}
                  >
                    {tier.badge}
                  </div>
                )}

                <div className="text-center mb-6 pt-2">
                  <tier.icon
                    className="w-8 h-8 mx-auto mb-3"
                    style={{ color: tier.color }}
                  />
                  <h3
                    className="colox-serif text-2xl font-semibold"
                    style={{ color: tier.color }}
                  >
                    {tier.name}
                  </h3>
                  <p
                    className="colox-serif text-sm italic mt-1"
                    style={{ color: "rgba(201,184,150,0.5)" }}
                  >
                    {tier.subtitle}
                  </p>
                </div>

                <div className="text-center mb-6">
                  <span
                    className="colox-serif text-4xl font-bold"
                    style={{ color: "#f5efe6" }}
                  >
                    {tier.price}
                  </span>
                  <p
                    className="colox-sans text-xs mt-1"
                    style={{ color: "rgba(212,162,84,0.4)" }}
                  >
                    {tier.meaning}
                  </p>
                </div>

                <div className="flex-1 space-y-3 mb-6">
                  {tier.items.map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <CheckCircle
                        className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                        style={{ color: tier.color, opacity: 0.7 }}
                      />
                      <span
                        className="colox-sans text-xs leading-relaxed"
                        style={{ color: "rgba(201,184,150,0.7)" }}
                      >
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  data-testid={`tier-cta-${tier.id}`}
                  onClick={() => handleCheckout(tier.id)}
                  disabled={checkoutLoading === tier.id}
                  className="w-full py-3 rounded-full colox-sans text-xs tracking-wider transition-all"
                  style={{
                    background: `linear-gradient(135deg, ${tier.color}22, ${tier.color}11)`,
                    border: `1px solid ${tier.color}33`,
                    color: tier.color,
                    opacity: checkoutLoading === tier.id ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = `linear-gradient(135deg, ${tier.color}44, ${tier.color}22)`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = `linear-gradient(135deg, ${tier.color}22, ${tier.color}11)`;
                  }}
                >
                  {checkoutLoading === tier.id ? 'Preparing checkout...' : tier.cta}
                </button>
              </motion.div>
            ))}
          </motion.div>

          {/* Angel number explanation */}
          <AnimSection className="mt-12 text-center">
            <p
              className="colox-sans text-xs italic"
              style={{ color: "rgba(212,162,84,0.35)" }}
            >
              Each price is a sacred angel number &mdash; new beginnings,
              creative expression, foundation, and divine wisdom.
            </p>
          </AnimSection>
        </div>
      </section>

      {/* ══════════════════════════════════════
           ROSE IMAGE INTERLUDE
         ══════════════════════════════════════ */}
      <section className="relative py-32 overflow-hidden">
        <img
          src={ASSETS.seedWaterGrow}
          alt="Sacred rose illustration"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.3, filter: "saturate(0.5) brightness(0.4)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, #0a0a0f, rgba(10,10,15,0.4), #0a0a0f)",
          }}
        />
        <div className="relative z-10 text-center px-6">
          <AnimSection>
            <p
              className="colox-sans text-xs tracking-[0.3em] mb-4"
              style={{ color: "rgba(212,162,84,0.5)" }}
            >
              FOR THE WOMAN WHO WALKS IN FAITH
            </p>
            <h2
              className="colox-serif text-3xl sm:text-5xl font-light italic mb-6"
              style={{ color: "#f5efe6" }}
            >
              "You don't leave your roots.
              <br />
              You discover new branches."
            </h2>
            <p
              className="colox-sans text-sm max-w-md mx-auto"
              style={{ color: "rgba(201,184,150,0.5)" }}
            >
              This book is for the believer who is ready to explore the beauty
              already present within her faith &mdash; the divine feminine energy
              that scripture has always held.
            </p>
          </AnimSection>
        </div>
      </section>

      {/* ══════════════════════════════════════
           KEEPSAKE COLLECTION
         ══════════════════════════════════════ */}
      <section
        id="keepsakes"
        className="py-24 px-6"
        style={{ background: "#0a0a0f" }}
      >
        <div className="max-w-7xl mx-auto">
          <AnimSection className="text-center mb-16">
            <p
              className="colox-sans text-xs tracking-[0.3em] mb-4"
              style={{ color: "rgba(212,162,84,0.5)" }}
            >
              COMPANION COLLECTION
            </p>
            <h2
              className="colox-serif text-4xl sm:text-5xl font-light mb-4"
              style={{ color: "#f5efe6" }}
            >
              Sacred Keepsakes
            </h2>
            <p
              className="colox-sans text-sm max-w-lg mx-auto"
              style={{ color: "rgba(201,184,150,0.5)" }}
            >
              Physical treasures to deepen your sacred coloring ritual. Each
              piece crafted with intention.
            </p>
          </AnimSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {KEEPSAKES.map((product, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`keepsake-card-${i}`}
                className="colox-card rounded-xl p-5 flex flex-col"
              >
                <product.icon
                  className="w-6 h-6 mb-4"
                  style={{ color: "#d4a254" }}
                />
                <h4
                  className="colox-serif text-lg font-semibold mb-2"
                  style={{ color: "#e8c87a" }}
                >
                  {product.name}
                </h4>
                <p
                  className="colox-sans text-xs leading-relaxed flex-1 mb-4"
                  style={{ color: "rgba(201,184,150,0.6)" }}
                >
                  {product.desc}
                </p>
                <div className="flex items-center justify-between">
                  <span
                    className="colox-serif text-lg font-bold"
                    style={{ color: "#f5efe6" }}
                  >
                    {product.price}
                  </span>
                  <button
                    onClick={addToCart}
                    className="colox-btn-outline px-4 py-1.5 rounded-full text-xs"
                  >
                    ADD
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════
           CREATOR / OUR STORY
         ══════════════════════════════════════ */}
      <section
        id="our-story"
        className="py-24 px-6 relative"
        style={{
          background:
            "linear-gradient(to bottom, #0a0a0f, #0f0c14, #0a0a0f)",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 70% 50%, rgba(212,162,84,0.04) 0%, transparent 50%)",
          }}
        />

        <div className="relative z-10 max-w-4xl mx-auto">
          <AnimSection>
            <div className="colox-card rounded-2xl p-8 md:p-12">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                {/* Creator Image */}
                <div className="flex-shrink-0">
                  <div
                    className="w-32 h-32 rounded-full overflow-hidden"
                    style={{
                      border: "2px solid rgba(212,162,84,0.2)",
                    }}
                  >
                    <img
                      src={ASSETS.yhwhIcon}
                      alt="Keeper Of The Fiyah"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Creator Text */}
                <div>
                  <p
                    className="colox-sans text-xs tracking-[0.2em] mb-3"
                    style={{ color: "rgba(212,162,84,0.5)" }}
                  >
                    FROM THE CREATOR
                  </p>
                  <blockquote
                    className="colox-serif text-lg italic leading-relaxed mb-4"
                    style={{ color: "#c9b896" }}
                  >
                    "These pages were curated with intention &mdash; each design,
                    each affirmation, each sacred pattern is meant to orient your
                    spirit toward divine connection. When you color, you
                    meditate. When you meditate, you connect. This is
                    YAHFFIRMATIONS in action."
                  </blockquote>
                  <div>
                    <p
                      className="colox-serif font-semibold"
                      style={{ color: "#d4a254" }}
                    >
                      Keeper Of The Fiyah
                    </p>
                    <p
                      className="colox-sans text-xs"
                      style={{ color: "rgba(201,184,150,0.4)" }}
                    >
                      Creator, INSPIYAH | A.R.T. Energi
                    </p>
                    <p
                      className="colox-sans text-xs mt-1"
                      style={{ color: "rgba(201,184,150,0.3)" }}
                    >
                      Authenticity | Reliability | Truth
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* ══════════════════════════════════════
           FINAL CTA
         ══════════════════════════════════════ */}
      <section className="py-24 px-6 relative overflow-hidden">
        {/* Cathedral background */}
        <img
          src={ASSETS.cathedral}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.1, filter: "saturate(0) brightness(0.5)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 50%, rgba(212,162,84,0.08), transparent 60%), linear-gradient(to bottom, rgba(10,10,15,0.8), #0a0a0f)",
          }}
        />

        <AnimSection className="relative z-10 text-center max-w-2xl mx-auto">
          <Flame
            className="w-8 h-8 mx-auto mb-6 animate-soft-pulse"
            style={{ color: "#d4a254" }}
          />
          <h2
            className="colox-serif text-4xl sm:text-5xl font-light mb-4"
            style={{ color: "#f5efe6" }}
          >
            Begin Your Sacred Journey
          </h2>
          <p
            className="colox-sans text-sm mb-8 leading-relaxed"
            style={{ color: "rgba(201,184,150,0.6)" }}
          >
            The light is already within you. This book simply helps you see it
            in color.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#tiers">
              <button
                data-testid="final-cta-btn"
                className="colox-btn-gold px-10 py-4 rounded-full text-sm"
              >
                SHOP THE COLLECTION
              </button>
            </a>
            <a
              href={ASSETS.seedPdf}
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="colox-btn-outline px-10 py-4 rounded-full text-sm">
                FREE SEED PAGES
              </button>
            </a>
          </div>
        </AnimSection>
      </section>

      {/* ══════════════════════════════════════
           FOOTER
         ══════════════════════════════════════ */}
      <footer
        data-testid="colox-footer"
        className="py-12 px-6"
        style={{
          background: "#07070a",
          borderTop: "1px solid rgba(212,162,84,0.08)",
        }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-10">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Flame className="w-4 h-4" style={{ color: "#d4a254" }} />
                <span
                  className="colox-serif text-lg font-semibold"
                  style={{ color: "#e8c87a" }}
                >
                  THE BOOK OF COLOX
                </span>
              </div>
              <p
                className="colox-sans text-xs leading-relaxed"
                style={{ color: "rgba(201,184,150,0.4)" }}
              >
                A sacred coloring experience by Keeper Of The Fiyah. Part of the
                INSPIYAH Ecosystem.
              </p>
            </div>

            {/* Links */}
            <div>
              <p
                className="colox-sans text-xs tracking-widest mb-4"
                style={{ color: "rgba(212,162,84,0.5)" }}
              >
                EXPLORE
              </p>
              <div className="space-y-2">
                {[
                  { label: "The Book", href: "#the-book" },
                  { label: "Choose Your Tier", href: "#tiers" },
                  { label: "Keepsake Collection", href: "#keepsakes" },
                  { label: "Our Story", href: "#our-story" },
                ].map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="block colox-sans text-xs hover:opacity-100 transition-opacity"
                    style={{ color: "rgba(201,184,150,0.4)" }}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Spirit */}
            <div>
              <p
                className="colox-sans text-xs tracking-widest mb-4"
                style={{ color: "rgba(212,162,84,0.5)" }}
              >
                SPIRIT
              </p>
              <p
                className="colox-serif italic text-sm mb-2"
                style={{ color: "rgba(201,184,150,0.4)" }}
              >
                "Let Yah Spirit Sear Its Word on Yah Soul"
              </p>
              <p
                className="colox-sans text-xs"
                style={{ color: "rgba(201,184,150,0.3)" }}
              >
                YAH HEALTH WEALTH HAPPINESS
              </p>
              <p
                className="colox-sans text-xs"
                style={{ color: "rgba(201,184,150,0.3)" }}
              >
                The Universal Pillars Of Success
              </p>
            </div>
          </div>

          <div className="colox-divider w-full mb-6" />

          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p
              className="colox-sans text-xs"
              style={{ color: "rgba(201,184,150,0.25)" }}
            >
              &copy; {new Date().getFullYear()} INSPIYAH | Keeper Of The Fiyah.
              All rights reserved.
            </p>
            <p
              className="colox-sans text-xs"
              style={{ color: "rgba(201,184,150,0.25)" }}
            >
              A.R.T. Energi &mdash; Authenticity | Reliability | Truth
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BookOfColox;
